import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactFlow, {
  addEdge,
  Background,
  BackgroundVariant,
  type Connection,
  Controls,
  MarkerType,
  MiniMap,
  type Node,
  useEdgesState,
  useNodesState,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  ArrowLeft,
  CheckCircle2,
  Save,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAvailableFunctions,
  useFlow,
  usePublishFlow,
  useUpdateFlow,
  useValidateFlow,
} from "@/features/flows/hooks";
import { cn } from "@/lib/utils";

type NodeCfg = {
  type: string;
  label: string;
  color: string;
  emoji: string;
  defaultData: Record<string, unknown>;
};

const NODE_TYPES: NodeCfg[] = [
  { type: "start", label: "Start", color: "#16a34a", emoji: "▶", defaultData: {} },
  {
    type: "message",
    label: "Message",
    color: "#2563eb",
    emoji: "💬",
    defaultData: { text: "Hello! How can I help you today?" },
  },
  {
    type: "condition",
    label: "Condition",
    color: "#ea580c",
    emoji: "◆",
    defaultData: { condition: "", true_label: "Yes", false_label: "No" },
  },
  {
    type: "input",
    label: "Collect Input",
    color: "#7c3aed",
    emoji: "⌨",
    defaultData: { variable: "", prompt: "Please provide your response." },
  },
  {
    type: "function_call",
    label: "Function Call",
    color: "#dc2626",
    emoji: "⚡",
    defaultData: { function_name: "", on_error: "continue" },
  },
  {
    type: "end",
    label: "End",
    color: "#475569",
    emoji: "■",
    defaultData: { outcome: "success" },
  },
];

const getCfg = (t: string) => NODE_TYPES.find((c) => c.type === t) ?? NODE_TYPES[0];

const nodeStyle = (t: string): React.CSSProperties => {
  const cfg = getCfg(t);
  return {
    background: cfg.color,
    color: "#fff",
    border: "none",
    borderRadius: t === "start" || t === "end" ? 24 : 8,
    padding: "10px 20px",
    fontSize: 12,
    fontWeight: 600,
    minWidth: 140,
    textAlign: "center",
  };
};

let _seq = 0;
const nextId = () => `node_${Date.now()}_${++_seq}`;

const DEFAULT_EDGE = {
  markerEnd: { type: MarkerType.ArrowClosed, color: "#94a3b8" },
  style: { stroke: "#94a3b8", strokeWidth: 2 },
};

const STATUS_STYLES: Record<string, string> = {
  published:
    "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400",
  draft:
    "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400",
  archived:
    "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
};

export default function FlowEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const flowQ = useFlow(id);
  const functionsQ = useAvailableFunctions();
  const update = useUpdateFlow(id!);
  const validate = useValidateFlow(id!);
  const publish = usePublishFlow(id!);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowName, setFlowName] = useState("");
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    const data = flowQ.data;
    if (!data) return;
    setFlowName(data.name);
    setNodes((data.nodes as Node[]) || []);
    setEdges((data.edges as never[]) || []);
    if (data.validation_errors?.length) {
      setValidationErrors(data.validation_errors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowQ.data]);

  const onConnect = useCallback(
    (c: Connection) => setEdges((eds) => addEdge({ ...c, ...DEFAULT_EDGE }, eds)),
    [setEdges],
  );

  const onNodeClick = useCallback(
    (_: React.MouseEvent, n: Node) => setSelectedNode(n),
    [],
  );
  const onPaneClick = useCallback(() => setSelectedNode(null), []);

  function addNode(t: string) {
    const cfg = getCfg(t);
    const newNode: Node = {
      id: nextId(),
      type: "default",
      position: {
        x: 100 + Math.random() * 200,
        y: 100 + nodes.length * 80 + Math.random() * 40,
      },
      data: {
        label: `${cfg.emoji} ${cfg.label}`,
        nodeType: t,
        config: { ...cfg.defaultData },
      },
      style: nodeStyle(t),
    };
    setNodes((ns) => [...ns, newNode]);
    setSelectedNode(newNode);
  }

  function deleteSelected() {
    if (!selectedNode) return;
    setNodes((ns) => ns.filter((n) => n.id !== selectedNode.id));
    setEdges((es) =>
      es.filter(
        (e) => e.source !== selectedNode.id && e.target !== selectedNode.id,
      ),
    );
    setSelectedNode(null);
  }

  function patchSelectedData(patch: Record<string, unknown>) {
    if (!selectedNode) return;
    const updated = {
      ...selectedNode,
      data: { ...selectedNode.data, ...patch },
    };
    setSelectedNode(updated);
    setNodes((ns) => ns.map((n) => (n.id === selectedNode.id ? updated : n)));
  }

  function patchSelectedConfig(patch: Record<string, unknown>) {
    if (!selectedNode) return;
    const cfg = (selectedNode.data as { config?: Record<string, unknown> })
      .config;
    patchSelectedData({ config: { ...(cfg ?? {}), ...patch } });
  }

  async function onSave() {
    try {
      await update.mutateAsync({ name: flowName, nodes, edges } as Partial<
        Parameters<typeof update.mutateAsync>[0]
      >);
      toast.success("Flow saved");
    } catch {
      toast.error("Failed to save flow");
    }
  }

  async function onValidate() {
    try {
      await update.mutateAsync({ name: flowName, nodes, edges } as Partial<
        Parameters<typeof update.mutateAsync>[0]
      >);
    } catch {
      toast.warning("Could not save — validating last saved version");
    }
    try {
      const r = await validate.mutateAsync();
      setValidationErrors(r.errors || []);
      if (r.is_valid) toast.success("Flow is valid");
      else toast.warning("Validation found errors — see banner");
    } catch {
      toast.error("Validation request failed");
    }
  }

  async function onPublish() {
    try {
      await publish.mutateAsync();
      toast.success("Flow published");
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || "Failed to publish flow");
    }
  }

  if (flowQ.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    );
  }

  if (flowQ.isError || !flowQ.data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/flows">
            <ArrowLeft className="size-4" />
            Back to Flows
          </Link>
        </Button>
        <Card>
          <CardContent className="py-10 text-center text-sm text-destructive">
            Failed to load flow. It may have been deleted.
          </CardContent>
        </Card>
      </div>
    );
  }

  const flow = flowQ.data;
  const selectedType = selectedNode?.data?.nodeType as string | undefined;
  const selectedCfg = (selectedNode?.data?.config ?? {}) as Record<
    string,
    string
  >;

  return (
    <div className="-mx-4 -my-6 flex h-[calc(100svh-64px)] flex-col md:-mx-6">
      <div className="flex flex-wrap items-center gap-2 border-b bg-background px-4 py-2.5 md:px-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/flows")}>
          <ArrowLeft className="size-4" />
          Flows
        </Button>
        <div className="mx-2 hidden h-6 w-px bg-border sm:block" />
        <Input
          value={flowName}
          onChange={(e) => setFlowName(e.target.value)}
          className="h-8 max-w-[280px] font-semibold"
        />
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
            STATUS_STYLES[flow.status] ?? "text-muted-foreground bg-muted",
          )}
        >
          {flow.status}
        </span>
        <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
          v{flow.version}
        </span>
        {flow.is_valid && (
          <span className="inline-flex items-center rounded-full border border-emerald-200 px-2 py-0.5 text-xs text-emerald-700 dark:border-emerald-900 dark:text-emerald-400">
            valid
          </span>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onSave}
            disabled={update.isPending}
          >
            <Save className="size-4" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onValidate}
            disabled={validate.isPending || update.isPending}
          >
            <CheckCircle2 className="size-4" />
            {validate.isPending ? "Validating…" : "Validate"}
          </Button>
          <Button
            size="sm"
            onClick={onPublish}
            disabled={
              !flow.is_valid || publish.isPending || flow.status === "published"
            }
          >
            <Send className="size-4" />
            {publish.isPending ? "Publishing…" : "Publish"}
          </Button>
        </div>
      </div>

      {validationErrors.length > 0 && (
        <div className="flex items-start gap-3 border-b bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 md:px-6">
          <div className="flex-1">
            {validationErrors.map((e, i) => (
              <div key={i}>• {e}</div>
            ))}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={() => setValidationErrors([])}
            aria-label="Dismiss validation errors"
          >
            <X className="size-4" />
          </Button>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        <aside className="flex w-44 shrink-0 flex-col gap-1.5 overflow-y-auto border-r p-3">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Add Node
          </p>
          {NODE_TYPES.map((cfg) => (
            <button
              key={cfg.type}
              type="button"
              onClick={() => addNode(cfg.type)}
              className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs font-medium transition-colors hover:bg-accent"
              style={{ borderColor: cfg.color, color: cfg.color }}
            >
              <span>{cfg.emoji}</span>
              <span>{cfg.label}</span>
            </button>
          ))}
          <div className="mt-2 border-t pt-2 text-[10px] leading-snug text-muted-foreground">
            Drag to reposition. Connect handles to create edges. Delete key
            removes selected.
          </div>
        </aside>

        <div className="relative flex-1 bg-muted/30">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            defaultEdgeOptions={DEFAULT_EDGE}
            fitView
            deleteKeyCode="Delete"
          >
            <Controls />
            <MiniMap nodeStrokeWidth={3} zoomable pannable />
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          </ReactFlow>
        </div>

        {selectedNode && (
          <aside className="flex w-80 shrink-0 flex-col gap-3 overflow-y-auto border-l p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-semibold">
                  {getCfg(selectedType ?? "").emoji}{" "}
                  {getCfg(selectedType ?? "").label} Node
                </p>
                <p className="text-[11px] text-muted-foreground">
                  ID: {selectedNode.id}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-destructive hover:text-destructive"
                onClick={deleteSelected}
                aria-label="Delete node"
              >
                <Trash2 className="size-4" />
              </Button>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="node-label">Display Label</Label>
              <Input
                id="node-label"
                value={(selectedNode.data?.label as string) ?? ""}
                onChange={(e) => patchSelectedData({ label: e.target.value })}
              />
            </div>

            {selectedType === "message" && (
              <div className="space-y-1.5">
                <Label>Message Text</Label>
                <Textarea
                  rows={3}
                  value={(selectedCfg.text as string) ?? ""}
                  onChange={(e) => patchSelectedConfig({ text: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  What the agent will say at this step.
                </p>
              </div>
            )}

            {selectedType === "condition" && (
              <>
                <div className="space-y-1.5">
                  <Label>Condition Expression</Label>
                  <Input
                    value={(selectedCfg.condition as string) ?? ""}
                    onChange={(e) =>
                      patchSelectedConfig({ condition: e.target.value })
                    }
                    placeholder='e.g. intent == "booking"'
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>True branch label</Label>
                  <Input
                    value={(selectedCfg.true_label as string) ?? "Yes"}
                    onChange={(e) =>
                      patchSelectedConfig({ true_label: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>False branch label</Label>
                  <Input
                    value={(selectedCfg.false_label as string) ?? "No"}
                    onChange={(e) =>
                      patchSelectedConfig({ false_label: e.target.value })
                    }
                  />
                </div>
              </>
            )}

            {selectedType === "input" && (
              <>
                <div className="space-y-1.5">
                  <Label>Variable Name</Label>
                  <Input
                    value={(selectedCfg.variable as string) ?? ""}
                    onChange={(e) =>
                      patchSelectedConfig({ variable: e.target.value })
                    }
                    placeholder="e.g. customer_name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Prompt Text</Label>
                  <Textarea
                    rows={2}
                    value={(selectedCfg.prompt as string) ?? ""}
                    onChange={(e) =>
                      patchSelectedConfig({ prompt: e.target.value })
                    }
                  />
                </div>
              </>
            )}

            {selectedType === "function_call" && (
              <>
                <div className="space-y-1.5">
                  <Label>Function</Label>
                  <Select
                    value={(selectedCfg.function_name as string) ?? ""}
                    onValueChange={(v) =>
                      patchSelectedConfig({ function_name: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a function" />
                    </SelectTrigger>
                    <SelectContent>
                      {(functionsQ.data ?? []).map((fn) => (
                        <SelectItem key={fn.name} value={fn.name}>
                          {fn.name}
                          <span className="ml-2 text-xs text-muted-foreground">
                            {fn.source}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(functionsQ.data?.length ?? 0) === 0 && (
                    <p className="text-xs text-muted-foreground">
                      No integration functions available. Connect an
                      integration first.
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>On Error</Label>
                  <Select
                    value={(selectedCfg.on_error as string) ?? "continue"}
                    onValueChange={(v) => patchSelectedConfig({ on_error: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="continue">Continue</SelectItem>
                      <SelectItem value="abort">Abort call</SelectItem>
                      <SelectItem value="retry">Retry (once)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {selectedType === "end" && (
              <div className="space-y-1.5">
                <Label>Outcome</Label>
                <Select
                  value={(selectedCfg.outcome as string) ?? "success"}
                  onValueChange={(v) => patchSelectedConfig({ outcome: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failure">Failure</SelectItem>
                    <SelectItem value="transfer">Transfer to human</SelectItem>
                    <SelectItem value="voicemail">Voicemail</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Outcome recorded on the call record.
                </p>
              </div>
            )}

            {selectedType === "start" && (
              <p className="text-sm text-muted-foreground">
                The Start node is the entry point of the flow. Only one Start
                node is allowed. Connect it to the first step.
              </p>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
